import { useCallback, useMemo, useState } from "react";
import { cloneDeep, isEqual } from "lodash-es";
import { useNavigate } from "react-router";
import { nanoid } from "nanoid";

import { ColumnMode } from "components/DragDropList/types";
import useUIConfig from "hooks/useUIConfig";
import { AnalyticsPageDashboard } from "hooks/useAnalytics/pages/dashboard";
import useTypedFlags from "hooks/useTypedFlags";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";

import { DashboardColumns, DashboardConfig } from "../types";
import {
  createInitialConfig,
  getAllDashboardWidgets,
  getInitialConfig,
  splitColumnsConfigBaseOnMode,
} from "../helpers";
import { VIEWS_LIMIT, DASHBOARD_STORAGE_KEY, DefaultDashboardTabs } from "../constants";
import useCurrentTab from "../useCurrentTab";
import { validateConfig } from "../validateConfig";

const useConfig = () => {
  const {
    numberOfDeploymentsWidgetFrontend,
    approvalPolicyEvaluationsWidgetFrontend,
    opentofuBackend,
  } = useTypedFlags();
  const { viewer } = useTypedContext(AccountContext);

  const allWidgets = useMemo(
    () =>
      getAllDashboardWidgets(
        {
          numberOfDeploymentsWidgetFrontend,
          approvalPolicyEvaluationsWidgetFrontend,
          opentofuBackend,
        },
        viewer.admin
      ),
    [
      numberOfDeploymentsWidgetFrontend,
      approvalPolicyEvaluationsWidgetFrontend,
      opentofuBackend,
      viewer.admin,
    ]
  );

  const initialConfig = getInitialConfig(
    {
      numberOfDeploymentsWidgetFrontend,
      approvalPolicyEvaluationsWidgetFrontend,
      opentofuBackend,
    },
    viewer.admin
  );

  const { config, updateConfig, configLoading } = useUIConfig<DashboardConfig>(
    DASHBOARD_STORAGE_KEY,
    initialConfig,
    validateConfig(
      {
        numberOfDeploymentsWidgetFrontend,
        approvalPolicyEvaluationsWidgetFrontend,
        opentofuBackend,
      },
      viewer.admin
    )
  );

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageDashboard.Dashboard,
  });

  const [shouldFocusOnInput, setShouldFocusOnInput] = useState(false);
  const [managementDrawerConfig, setManagementDrawerConfig] = useState(config);

  const managementDrawerTabs = Object.entries(managementDrawerConfig).map(([id, value]) => ({
    id,
    title: value.title,
  }));

  const updateManagementDrawerTabColumnsConfig = useCallback(
    (tab: string, columns: DashboardColumns) => {
      const newConfig = {
        ...managementDrawerConfig,
        [tab]: {
          ...managementDrawerConfig[tab],
          ...columns,
        },
      };

      setManagementDrawerConfig(newConfig);
    },
    [managementDrawerConfig]
  );

  const updateManagementDrawerTabColumnModeConfig = useCallback(
    (tab: string, columnMode: ColumnMode) => {
      setManagementDrawerConfig({
        ...managementDrawerConfig,
        [tab]: {
          ...managementDrawerConfig[tab],
          ...splitColumnsConfigBaseOnMode(
            [...managementDrawerConfig[tab].left, ...managementDrawerConfig[tab].right],
            columnMode
          ),
        },
      });
    },
    [managementDrawerConfig]
  );

  const navigate = useNavigate();
  const currentTab = useCurrentTab();
  const goToFirstTab = useCallback(
    () => navigate(`/dashboard?tab=${DefaultDashboardTabs.Overview}`),
    [navigate]
  );

  const saveManagementDrawerChanges = useCallback(() => {
    updateConfig(managementDrawerConfig);
    trackSegmentAnalyticsEvent("Dashboard - Configuration changed", managementDrawerConfig);

    if (!Object.keys(managementDrawerConfig).includes(currentTab)) {
      goToFirstTab();
    }
  }, [updateConfig, managementDrawerConfig, currentTab, goToFirstTab, trackSegmentAnalyticsEvent]);

  const [currentManageDrawerTab, setCurrentManageDrawerTab] = useState(currentTab);

  const setCurrentManageDrawerTabHandler = useCallback((tab: string) => {
    setCurrentManageDrawerTab(tab);
    setShouldFocusOnInput(false);
  }, []);

  const addNewTabToManagementDrawerConfig = useCallback(
    (prevConfig: DashboardConfig) => {
      const newKey = nanoid();
      const newConfig = {
        ...prevConfig,
        [newKey]: {
          ...createInitialConfig(allWidgets, ColumnMode.Double),
          title: "New tab",
        },
      };

      setManagementDrawerConfig(newConfig);
      setCurrentManageDrawerTab(newKey);
    },
    [allWidgets]
  );

  const addNewTab = useCallback(() => {
    addNewTabToManagementDrawerConfig(managementDrawerConfig);
    setShouldFocusOnInput(true);
  }, [managementDrawerConfig, addNewTabToManagementDrawerConfig]);

  const addNewTabInDrawer = useCallback(() => {
    trackSegmentAnalyticsEvent("Dashboard - Create New View", {
      method: "manage dashboard drawer tab click",
    });
    addNewTab();
  }, [addNewTab, trackSegmentAnalyticsEvent]);

  const changeTabName = useCallback(
    (id: string, value: string) => {
      setManagementDrawerConfig({
        ...managementDrawerConfig,
        [id]: {
          ...managementDrawerConfig[id],
          title: value,
        },
      });
    },
    [managementDrawerConfig]
  );

  const removeTabInDrawer = useCallback(
    (id: string) => {
      const newManagementDrawerConfig = cloneDeep(managementDrawerConfig);
      if (newManagementDrawerConfig[id]) {
        delete newManagementDrawerConfig[id];
        setManagementDrawerConfig(newManagementDrawerConfig);

        if (id === currentManageDrawerTab) {
          setCurrentManageDrawerTab(DefaultDashboardTabs.Overview);
        }
      }
    },
    [managementDrawerConfig, currentManageDrawerTab]
  );

  const isManagementDrawerConfigModified = useMemo(
    () => !isEqual(managementDrawerConfig, config),
    [managementDrawerConfig, config]
  );

  const canAddMoreManagementDrawerViews = useMemo(
    () => Object.keys(managementDrawerConfig).length < VIEWS_LIMIT,
    [managementDrawerConfig]
  );

  return {
    managementDrawerTabs,
    updateManagementDrawerTabColumnsConfig,
    updateManagementDrawerTabColumnModeConfig,
    saveManagementDrawerChanges,
    managementDrawerConfig,
    isManagementDrawerConfigModified,
    addNewTabInDrawer,
    removeTabInDrawer,
    changeTabName,
    setCurrentManageDrawerTab: setCurrentManageDrawerTabHandler,
    currentManageDrawerTab,
    canAddMoreManagementDrawerViews,
    configLoading,
    addNewTab,
    shouldFocusOnInput,
  };
};

export default useConfig;
